import React from 'react';
import { graphql, navigate } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  standardColours,
} from '../styles';
import { Link, Overline } from './ui';
import { buildUrl } from '../utils';

const StyledCaseStudyCard = styled.article`
  background-color: ${standardColours.white};
  border: solid 1px ${standardColours.grey};
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;

  ${({ isFeatured }) => {
    if (isFeatured) {
      return css`
        display: grid;
        background-color: ${standardColours.lightGrey};

        ${minBreakpointQuery.smedium`
          grid-template-columns: repeat(2, 1fr);
          gap: 30px;
          padding: 30px;
        `}

        ${minBreakpointQuery.mlarge`
          gap: 35px;
          padding: 35px;
        `}

        ${minBreakpointQuery.large`
          gap: 40px;
          padding: 40px;
        `}
      `;
    }
  }}
`;

const StyledImage = styled(GatsbyImage)`
  border-radius: 20px;
`;

const StyledContent = styled.div`
  padding: 0 30px 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${minBreakpointQuery.small`
    padding-right: 40px;
    padding-bottom: 30px;
    padding-left: 40px;
  `}

  ${minBreakpointQuery.large`
    padding-right: 50px;
    padding-bottom: 40px;
    padding-left: 50px;
  `}

  ${({ isFeatured }) => {
    if (isFeatured) {
      return css`
        ${minBreakpointQuery.smedium`
          padding-right: 20px;
          padding-bottom: 20px;
          padding-left: 20px;
        `}

        ${minBreakpointQuery.large`
          padding-bottom: 25px;
        `}
      `;
    }
  }}
`;

const StyledTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  padding: 15px 0;
  border-bottom: solid 1px ${standardColours.grey};

  ${minBreakpointQuery.small`
    padding-top: 20px;
    padding-bottom: 20px;
  `}

  ${minBreakpointQuery.large`
    padding-top: 25px;
    padding-bottom: 25px;
  `}
`;

const StyledLogo = styled.img`
  height: 50px;
  max-width: 150px;
`;

const StyledHeading = styled.h3`
  margin-top: 15px;
  font-weight: ${fontWeights.light};
  ${fluidFontSize(
    '22px',
    '30px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge
  )};

  ${minBreakpointQuery.small`
    margin-top: 20px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 25px;
  `}
`;

const StyledText = styled.p`
  margin-top: 30px;

  ${minBreakpointQuery.small`
    margin-top: 35px;
    ${fontSize(17)};
  `}

  ${minBreakpointQuery.large`
    margin-top: 40px;
    ${fontSize(18)};
  `}
`;

const CaseStudyCard = caseStudy => {
  const { heading, image, organisationName, organisationLogo, isFeatured } =
    caseStudy;

  return (
    <StyledCaseStudyCard
      isFeatured={isFeatured}
      onClick={() => navigate(buildUrl(caseStudy.slug, caseStudy))}
    >
      <StyledImage image={image.gatsbyImageData} alt={image.alt} />
      <StyledContent isFeatured={isFeatured}>
        <StyledTop>
          <Overline>
            {isFeatured ? 'Featured Case Study' : 'Case Study'}
          </Overline>
          <StyledLogo
            src={organisationLogo.url}
            alt={organisationLogo.alt}
            loading="lazy"
          />
        </StyledTop>
        <StyledHeading>
          <Link to={caseStudy}>{heading}</Link>
        </StyledHeading>
        <StyledText>Charitylog for {organisationName}</StyledText>
      </StyledContent>
    </StyledCaseStudyCard>
  );
};

export default CaseStudyCard;

export const CaseStudyCardFragment = graphql`
  fragment CaseStudyCardFragment on DatoCmsCaseStudy {
    heading: title
    image: featuredImage {
      gatsbyImageData(width: 710, height: 330)
      alt
    }
    organisationName
    organisationLogo {
      url
      alt
    }
    ...LinkFragment
  }
`;
